class styleGuidePage extends HTMLElement {
  constructor() {
    super();
    this.DEV = true; // set to false in production to remove console logs

    this.forceDefault = false; // set true if you want to set a default page with javascript
    this.firstLevelParameter = 'first_level';
    this.secondLevelParameter = 'second_level';

    this.firstLevelButtons = this.querySelectorAll('[data-js-firstLevelButton]');
    this.secondLevelButtons = this.querySelectorAll('[data-js-secondLevelButton]');
    this.firstLevelContainers = this.querySelectorAll('[data-js-firstLevelContainer]');
    this.secondLevelContainers = this.querySelectorAll('[data-js-secondLevelContainer]');

    if (this.DEV) {
      console.log('styleGuidePage constructor', {
        firstLevelButtons: this.firstLevelButtons,
        secondLevelButtons: this.secondLevelButtons,
        firstLevelContainers: this.firstLevelContainers,
        secondLevelContainers: this.secondLevelContainers,
      });
    }
  }

  /**
   * Called when the element is added to the DOM
   * @returns {void}
   */
  connectedCallback() {
    const firstLevelParameter = UrlUtils.getQueryParam(this.firstLevelParameter);
    const secondLevelParameter = UrlUtils.getQueryParam(this.secondLevelParameter);

    if (this.DEV) {
      console.log("firstLevelParameter", firstLevelParameter);
      console.log("secondLevelParameter", secondLevelParameter);
    }

    if(firstLevelParameter || this.forceDefault) this.setPage(firstLevelParameter, secondLevelParameter);
    this.setupEventListeners();
  }

  /**
   * Set the page based on the selected options (if any)
   * @param {string} firstLevelOptionSelected - The area of the first level button
   * @param {string} secondLevelOptionSelected - The area of the second level button
   * @returns {void}
   */
  setPage(firstLevelOptionSelected = null, secondLevelOptionSelected = null) {
    this.inactiveAll();

    if (!firstLevelOptionSelected) {
      this.activateDefault();
    } else if (firstLevelOptionSelected && !secondLevelOptionSelected) {
      this.activateFirstLevel(firstLevelOptionSelected);
    } else {
      this.activateFirstAndSecondLevel(firstLevelOptionSelected, secondLevelOptionSelected);
    }
  }

  /**
   * Reset all buttons and containers to inactive state
   * @returns {void}
   */
  inactiveAll() {
    this.firstLevelContainers.forEach(container => {
      this.activateContainer(container, false);
    });
    this.secondLevelContainers.forEach(container => {
      this.activateContainer(container, false);
    });
    this.firstLevelButtons.forEach(button => {
      this.activateButton(button, false);
    });
    this.secondLevelButtons.forEach(button => {
      this.activateButton(button, false);
    });
  }

  /**
   * Activate the default page status
   * @returns {void}
   */
  activateDefault() {
    if (this.DEV) console.log("Default activation");
  
    this.activateButton(this.firstLevelButtons[0], true);
    this.activateContainer(this.firstLevelContainers[0], true);
    

    const secondLevelButtons = this.firstLevelContainers[0].querySelectorAll('[data-js-secondLevelButton]');
    const secondLevelContainers = this.firstLevelContainers[0].querySelectorAll('[data-js-secondLevelContainer]');

    this.activateButton(secondLevelButtons[0], true);
    this.activateContainer(secondLevelContainers[0], true);
    
  }

  /**
   * Activate the first and second levels based on the selected options
   * @param {string} firstLevelOptionSelected - The area of the first level button
   * @param {string} secondLevelOptionSelected - The area of the second level button
   * @returns {void}
   */
  activateFirstAndSecondLevel(firstLevelOptionSelected, secondLevelOptionSelected) {
    if (this.DEV) console.log("Activating first and second levels:", { firstLevelOptionSelected, secondLevelOptionSelected });

    this.firstLevelButtons.forEach(button => this.activateButton(button, button.dataset.area === firstLevelOptionSelected));
    this.firstLevelContainers.forEach(container => {
      const isActive = container.dataset.area === firstLevelOptionSelected;
      this.activateContainer(container, isActive);

      if (isActive) {
        const secondLevelButtons = container.querySelectorAll('[data-js-secondLevelButton]');
        const secondLevelContainers = container.querySelectorAll('[data-js-secondLevelContainer]');

        const secondLevelFound = [...secondLevelButtons].some(button => button.dataset.area === secondLevelOptionSelected);
        if (this.DEV) console.log("Second level found?", secondLevelFound);

        if (secondLevelFound) {
          secondLevelButtons.forEach(button => this.activateButton(button, button.dataset.area === secondLevelOptionSelected));
          secondLevelContainers.forEach(container => this.activateContainer(container, container.dataset.area === secondLevelOptionSelected));
        } else {
          this.activateButton(secondLevelButtons[0], true);
          this.activateContainer(secondLevelContainers[0], true);
        }
      }
    });
  }

  /**
   * Set up event listeners for all buttons
   */
  setupEventListeners() {
    [...this.firstLevelButtons, ...this.secondLevelButtons].forEach(button => {
      button.addEventListener('click', this.handleClick.bind(this));
    });
  }

  /**
   * Handle click events for buttons
   * @param {Event} event - The click event
   */
  handleClick(event) {

    const target = event.currentTarget;
    if (target.hasAttribute('active')) return;

    const isFirstLevel = target.hasAttribute('data-js-firstLevelButton');
    const optionClicked = target.dataset.area;

    if (this.DEV) console.log(`${isFirstLevel ? "First" : "Second"} level button clicked:`, target);

    if (isFirstLevel) {
      this.activateFirstLevel(optionClicked);
    } else {
      this.activateSecondLevel(optionClicked);
    }
  }

  /**
   * Activate the first level based on the clicked button
   * @param {string} optionClicked - The area of the clicked button
   */
  activateFirstLevel(optionClicked) {
    if (this.DEV) console.log("Activating first level:", optionClicked);

    this.firstLevelButtons.forEach(button => this.activateButton(button, button.dataset.area === optionClicked));
    this.firstLevelContainers.forEach(container => {
      const isActive = container.dataset.area === optionClicked;
      this.activateContainer(container, isActive);

      if (isActive) {
        const secondLevelButtons = container.querySelectorAll('[data-js-secondLevelButton]');
        const secondLevelContainers = container.querySelectorAll('[data-js-secondLevelContainer]');
        this.activateContainer(secondLevelContainers[0], true);
        this.activateButton(secondLevelButtons[0], true);
      }
    });
  }

  /**
   * Activate the second level based on the clicked button
   * @param {string} optionClicked - The area of the clicked button
   */
  activateSecondLevel(optionClicked) {
    if (this.DEV) console.log("Activating second level:", optionClicked);

    this.secondLevelContainers.forEach(container => this.activateContainer(container, container.dataset.area === optionClicked));
    this.secondLevelButtons.forEach(button => this.activateButton(button, button.dataset.area === optionClicked));
  }

  /**
   * Activate or deactivate a container (only alias of a function)
   * @param {HTMLElement} container - The container element
   * @param {boolean} active - Whether to activate or deactivate the container
   */
  activateContainer(container, active) {
    container.toggleAttribute('active', active);
  }

  /**
   * Activate or deactivate a button (only alias of a function)
   * @param {HTMLElement} button - The button element
   * @param {boolean} active - Whether to activate or deactivate the button
   */
  activateButton(button, active) {
    button.toggleAttribute('active', active);
  }
}

customElements.define('style-guide-page', styleGuidePage);
